import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL, getToken } from "@/lib/api";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Download, Star, QrCode } from "lucide-react";
import { toast } from "sonner";

export default function Leads() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", title: "", interest_rating: 3, notes: "", follow_up_at: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/leads").then(({ data }) => setLeads(data.leads));
  }, [user, loading]);

  const add = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", { ...form, interest_rating: Number(form.interest_rating) });
      setLeads((x) => [data.lead, ...x]);
      setForm({ name: "", email: "", company: "", title: "", interest_rating: 3, notes: "", follow_up_at: "" });
      toast.success("Lead captured.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const exportCsv = () => {
    fetch(`${BACKEND_URL}/api/leads/export.csv`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob()).then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "leads.csv"; a.click();
      });
  };

  return (
    <Layout>
      <section className="container-summit py-12 md:py-16">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="overline mb-2"><span className="accent-rule" />Digital Lead Capture</div>
            <h1 className="text-4xl sm:text-5xl">Your day-of pipeline.</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} className="btn-outline-black" data-testid="leads-export"><Download size={16} /> Export CSV</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
          <form onSubmit={add} className="card-flat p-6 grid gap-4 self-start">
            <h2 className="text-lg">Capture lead</h2>
            <F label="Name" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 rounded-none" data-testid="lead-name" /></F>
            <F label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-none" data-testid="lead-email" /></F>
            <F label="Company"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="h-11 rounded-none" /></F>
            <F label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-none" /></F>
            <div>
              <Label className="overline">Interest (1–5)</Label>
              <div className="flex items-center gap-1 mt-2">
                {[1,2,3,4,5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, interest_rating: n })} className="p-1" data-testid={`lead-rating-${n}`}>
                    <Star size={20} className={n <= form.interest_rating ? "fill-[#A39171] text-[#A39171]" : "text-zinc-300"} />
                  </button>
                ))}
              </div>
            </div>
            <F label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" /></F>
            <F label="Follow-up date (optional)"><Input type="date" value={form.follow_up_at} onChange={(e) => setForm({ ...form, follow_up_at: e.target.value })} className="h-11 rounded-none" /></F>
            <button disabled={submitting} className="btn-black" data-testid="lead-submit"><Plus size={14} /> {submitting ? "Saving…" : "Add Lead"}</button>
          </form>
          <div className="grid gap-3">
            {leads.map((l) => (
              <div key={l.id} className="card-flat p-5 grid md:grid-cols-[1fr_auto] gap-3" data-testid={`lead-row-${l.id}`}>
                <div>
                  <div className="font-medium">{l.name} <span className="text-zinc-500">· {l.company || "—"}</span></div>
                  <div className="text-xs text-zinc-500">{l.title} · {l.email}</div>
                  {l.notes && <p className="text-sm text-zinc-600 mt-2">{l.notes}</p>}
                </div>
                <div className="flex items-start gap-1">{[...Array(l.interest_rating || 0)].map((_,i) => <Star key={i} size={14} className="fill-[#A39171] text-[#A39171]" />)}</div>
              </div>
            ))}
            {leads.length === 0 && <div className="card-flat p-10 text-center text-zinc-500 text-sm">
              <QrCode size={28} className="mx-auto text-zinc-300 mb-3" />
              No leads yet — capture your first one using the form on the left.
            </div>}
          </div>
        </div>
      </section>
    </Layout>
  );
}

function F({ label, required, children }) {
  return (<div><Label className="overline">{label}{required && <span className="text-[#A39171] ml-1">*</span>}</Label><div className="mt-2">{children}</div></div>);
}
